import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const riskColor = (r: string) => r === 'critical' ? 'bg-red-100 text-red-700 border-red-200' : r === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' : r === 'moderate' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200';

export default function SWTrackingPage() {
  const { user } = useAuth();
  const [caseStatuses, setCaseStatuses] = useState<Record<string, string>>({});
  const [showStatusModal, setShowStatusModal] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [myCases, setMyCases] = useState<any[]>([]);


  useEffect(() => {
    fetch(`https://guardiancloudcare.com/backend/api/get_cases.php?uid=${user?.uid}`)
    .then(async (res) => {

      const text = await res.text();

      console.log(text);

      return JSON.parse(text);

    })
    .then(data => {

      if (Array.isArray(data)) {
        setMyCases(data);
      } else {
        console.error(data);
        setMyCases([]);
      }

    })
    .catch(err => {
      console.error("Fetch Error:", err);
      setMyCases([]);
    });
  }, []);

  const getRisk = (c: any) => {
    let score = 75;

    switch (c.incident_type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;

      case 'Physical Abuse':
        score = 90;
        break;

      case 'Emotional Abuse':
        score = 80;
        break;

      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;

      default:
        score = 70;
    }

    if (c.prior_incidents === 'yes') score += 5;
    if (c.priority_level === 'urgent') score += 3;
    if (c.priority_level === 'emergency') score += 5;

    if (score > 100) score = 100;

    let level = 'low';
    if (score >= 90) level = 'critical';
    else if (score >= 80) level = 'high';
    else if (score >= 75) level = 'moderate';

    return { score, level };
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;

    await fetch("https://guardiancloudcare.com/backend/api/update_case.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        case_number: showStatusModal,
        case_status: newStatus,
        progress_note: statusNote,
        uid: user?.uid
      })
    });

    fetch(`https://guardiancloudcare.com/backend/api/get_cases.php?uid=${user?.uid}`)
    .then(res => res.json())
    .then(data => {

      if (Array.isArray(data)) {
        setMyCases(data);
      } else {
        setMyCases([]);
      }

    });

    setShowStatusModal(null);
    setStatusNote('');
    setNewStatus('');
  };

  return (
    <AppLayout title="Case Tracking" breadcrumb={['Social Worker', 'Case Tracking']}>
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-2">Case Status Workflow</h3>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {['Open', 'Ongoing', 'For Approval', 'Escalated', 'Closed'].map((s, i, arr) => (
              <div key={s} className="flex items-center gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full font-medium">{s}</span>
                {i < arr.length - 1 && <i className="ri-arrow-right-line text-gray-300 text-sm" />}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400">System auto-flags delayed cases and notifies supervisor. Supervisor approval required for &quot;For Approval&quot; and &quot;Closed&quot; statuses.</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100"><h3 className="font-semibold text-gray-800">Update Case Status</h3></div>
          <div className="divide-y divide-gray-50">
            {myCases.map((c) => {
              const risk = getRisk(c);

              return (
                <div key={c.case_number} className="px-5 py-4 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">
                      {c.first_name} {c.last_name}
                    </p>

                    <p className="text-xs font-mono text-gray-400">
                      {c.case_number} · {c.incident_type}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${riskColor(risk.level)}`}>
                      {risk.level} ({risk.score})
                    </span>

                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium capitalize">
                      {caseStatuses[c.case_number] || (c.case_status || 'No Status')}
                    </span>

                    <button
                      onClick={() => { setShowStatusModal(c.case_number); setNewStatus(''); }}
                      className="text-xs bg-teal-500 text-white px-3 py-1.5 rounded-lg hover:bg-teal-600"
                    >
                      Update
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showStatusModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Update Case Status</h3>
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">New Status</label>
              <div className="grid grid-cols-2 gap-2">
                {['Open', 'Ongoing', 'For Approval', 'Escalated', 'Closed'].map((s) => (
                  <button key={s} onClick={() => setNewStatus(s)}
                    className={`py-2.5 px-3 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer ${newStatus === s ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Progress Note</label>
              <textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={3} maxLength={300} placeholder="Describe the progress update..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" />
            </div> 
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowStatusModal(null)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={handleStatusUpdate} disabled={!newStatus} className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 cursor-pointer whitespace-nowrap">Update Status</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
